import { AttachFile, ErrorOutline, ExpandMore, UploadFileOutlined } from '@mui/icons-material'
import CloseIcon from '@mui/icons-material/Close'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material'
import { Grid } from '@mui/system'
import { useMutation } from '@tanstack/react-query'
import { MuiFileInput } from 'mui-file-input'
import React, { FunctionComponent, useState } from 'react'

import {
  StyledTable,
  StyledTableBody,
  StyledTableContainer,
  StyledTableHead,
} from '../../../../base/components/MuiTable/StyledTable.tsx'
import {
  MyAccordion,
  MyAccordionDetails,
  MyAccordionSummary,
} from '../../../../base/components/MyAccordion/MyAccordion.tsx'
import { useError } from '../../../../base/contexts/ErrorProvider.tsx'
import { swalClose, swalLoading } from '../../../../utils/swal'
import { apiArticuloImportarActualizar } from '../../api/apiArticuloImportarActualizar.ts'
import { useArticuloOperacion } from '../../hook/useArticuloOperacion.tsx'

interface OwnProps {
  id: string
  keepMounted: boolean
  open: boolean
  onClose: (value?: boolean) => void
}

type Props = OwnProps

/**
 * Modulo de importación de articulos masivo
 * @param props
 * @constructor
 */
const ArticuloImportarActualizarDialog: FunctionComponent<Props> = (props) => {
  const { onClose, open, ...other } = props
  const [file, setFile] = useState<File | null>(null)
  const { showError } = useError()

  const { exportarDatos } = useArticuloOperacion()

  const { mutate, data, isPending, isError, error, reset } = useMutation({
    mutationFn: (fileToUpload: File) => apiArticuloImportarActualizar(fileToUpload),
    onMutate: () => {
      swalLoading('Actualización masiva...')
    },
    onSettled: () => {
      swalClose()
    },
    onSuccess: (res) => {
      if (res.success) {
        setFile(null)
        setTimeout(() => {
          onClose(true)
        }, 15000)
      }
    },
    onError: (err) => {
      showError(err)
    },
  })

  /** Evento onchange cuando selecciona el archivo */
  const handleFileChange = (selectedFile: File | null) => {
    if (selectedFile) {
      setFile(selectedFile)
      reset()
    }
  }

  /**
   * Envia el archivo al servidor
   */
  const handleUpload = () => {
    if (file) mutate(file)
  }

  /** Funcion de limpieza total */
  const handleExited = () => {
    setFile(null)
    reset()
  }

  return (
    <Dialog
      maxWidth="md"
      open={open}
      disableEnforceFocus
      disableAutoFocus
      slotProps={{
        transition: { onExited: handleExited },
      }}
      {...other}
    >
      <DialogTitle>Importar articulos</DialogTitle>
      <DialogContent dividers>
        <MyAccordion defaultExpanded>
          <MyAccordionSummary expandIcon={<ExpandMore />}>
            <Typography component="span" fontWeight={600}>
              Instrucciones para la actualización masiva
            </Typography>
          </MyAccordionSummary>
          <MyAccordionDetails>
            <Alert severity={'info'} icon={false}>
              <Box>
                <Typography gutterBottom variant={'subtitle2'}>
                  Siga estos pasos para actualizar sus artículos de forma masiva:
                </Typography>

                <ol
                  style={{
                    margin: 0,
                    paddingLeft: 20,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                  }}
                >
                  <li>
                    <b>Descargue la base actual:</b> Genere el archivo con sus articulos (max: 1000) actuales
                    haciendo clic aquí:
                    <div style={{ marginTop: 8 }}>
                      <Button
                        variant="outlined"
                        color="secondary"
                        size="small"
                        onClick={() => exportarDatos(true)}
                      >
                        Descargar archivo base actualizador
                      </Button>
                    </div>
                  </li>

                  <li>
                    <b>Filtre el archivo:</b> Abra el CSV y <b>elimine las filas</b> de los artículos que NO
                    desea actualizar. Deje únicamente los que va a modificar para que el proceso sea más
                    rápido y seguro.
                  </li>

                  <li>
                    <b>Modifique los datos:</b> Realice sus cambios respetando las siguientes reglas
                    estrictas:
                    <ul
                      style={{
                        marginTop: 8,
                        paddingLeft: 20,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px',
                      }}
                    >
                      <li>
                        No modifique ni elimine los <b>encabezados</b> (la primera fila).
                      </li>
                      <li>
                        No altere la columna <b>codigoArticulo</b>, es el identificador único del sistema.
                      </li>
                      <li>
                        Los campos booleanos (opciones) solo aceptan los valores <b>"SI"</b> o <b>"NO"</b>.
                      </li>
                      <li>
                        Si asigna varios proveedores a un artículo, separe sus códigos por punto y coma{' '}
                        <b>(;)</b>.
                      </li>
                    </ul>
                  </li>

                  <li>
                    <b>Guarde y suba el archivo:</b> Asegúrese de que el archivo final mantenga el formato{' '}
                    <b>CSV (separado por punto y coma ;)</b>.
                    <br />
                    <small style={{ color: 'text.secondary' }}>* El tamaño máximo permitido es de 1MB.</small>
                  </li>
                </ol>
                <Alert severity={'warning'} icon={false} sx={{ mt: 2 }}>
                  <Typography variant={'caption'}>
                    <b>Nota:</b> Revise que los datos sean correctos antes de importar. Si el archivo contiene
                    un código de artículo que no existe en el sistema, esa fila será omitida automáticamente.
                  </Typography>
                </Alert>
              </Box>
            </Alert>
          </MyAccordionDetails>
        </MyAccordion>

        <Grid container>
          <Grid size={12}>
            <MuiFileInput
              size={'medium'}
              fullWidth
              sx={{ mt: 3, mb: 0.5 }}
              label={'Archivo separado por ;'}
              value={file}
              slotProps={{
                htmlInput: { accept: '.csv' },
                input: {
                  startAdornment: <AttachFile />,
                },
              }}
              placeholder="Clic aqui para seleccionar archivo csv"
              clearIconButtonProps={{
                title: 'Quitar',
                children: <CloseIcon fontSize="small" />,
                onClick: () => {
                  setFile(null)
                },
              }}
              onChange={handleFileChange}
            />
            <small>Archivo seleccionado: {file ? file.name : 'NINGUNO'}</small>
          </Grid>
          <Grid size={12}>
            {isError && (
              <Alert severity="error" icon={<ErrorOutline />}>
                Ocurrió un error crítico: {(error as Error).message}
              </Alert>
            )}

            {data?.success && (
              <Box>
                <Alert severity="success">
                  <Typography>¡Actualización masiva completada con éxito! {data.estado}</Typography>
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {data.estadisticas.map((item) => (
                      <li key={item.codigo}>
                        {item.codigo}: {item.descripcion}
                      </li>
                    ))}
                  </ul>
                </Alert>

                <small>La ventana se cerrará en 15 segundos o presione el botón "CERRAR"...</small>
              </Box>
            )}

            {data && !data.success && data.detalle.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                  Se procesó el archivo de actualización, pero se encontraron errores en {data.detalle.length}{' '}
                  filas.
                </Alert>
                <StyledTableContainer sx={{ maxHeight: 410 }}>
                  <StyledTable stickyHeader>
                    <StyledTableHead bgColor={'error'}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Fila</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Articulo</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Errores Detectados</TableCell>
                      </TableRow>
                    </StyledTableHead>
                    <StyledTableBody bgColor={'error'} striped hover={true}>
                      {data.detalle.map((err, index) => (
                        <TableRow key={index} hover>
                          <TableCell>{err.fila}</TableCell>
                          <TableCell>
                            <code>{err.columna}</code>
                          </TableCell>
                          <TableCell color="error.main">
                            {Array.isArray(err.errores) ? err.errores.join(', ') : err.errores}
                          </TableCell>
                        </TableRow>
                      ))}
                    </StyledTableBody>
                  </StyledTable>
                </StyledTableContainer>
              </Box>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center' }}>
        <Button
          color={'error'}
          disabled={isPending}
          onClick={() => {
            onClose(!!data)
          }}
        >
          Cerrar
        </Button>
        <Button
          color={'info'}
          loading={isPending}
          variant={'contained'}
          startIcon={<UploadFileOutlined />}
          onClick={handleUpload}
          disabled={!file}
        >
          Iniciar Actualización masiva
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ArticuloImportarActualizarDialog
